import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "components/GlobalSnackbar";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
} from "@mui/material";
import { buttonStyles } from "utils/buttonStyles";
import { dialogHeaderStyles, dialogContentStyles } from "utils/dialogStyles";

import { apiTambahInbois, apiGetMaxNoInbois, apiGetMaxNoInboisLike4 } from "api/inbois";
import { apiGetAkaunByNoAkaun } from "api/akaun";
import { apiTambahLejar } from "api/lejar";
import { handleApiError } from "utils/errorHandler";

function TambahInbois({ open, onClose, selectedAkaun, onSuccess }) {
  const { showSuccess, showError, showWarning } = useSnackbar();
  const [tarikh_inbois, setTarikhInbois] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [newNoInbois, setNewNoInbois] = useState("");
  // Get user info from localStorage
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();

  // Reset fields to empty every time popup is opened
  React.useEffect(() => {
    if (open) {
      setTarikhInbois("");
      setError("");
      setNewNoInbois("");
      (async () => {
        try {
          // Use new endpoint for max no_inbois like '4%' and ends with digit
          const maxNoInbois = await apiGetMaxNoInboisLike4();
          if (maxNoInbois && !isNaN(Number(maxNoInbois))) {
            setNewNoInbois(String(Number(maxNoInbois) + 1));
          } else {
            setNewNoInbois("401100010925");
          }
        } catch {
          setNewNoInbois("");
        }
      })();
    }
  }, [open, selectedAkaun]);

  const handleSimpan = async () => {
    setError("");
    if (!tarikh_inbois) {
      showError("Sila isi semua maklumat wajib.");
      return;
    }
    // Client-side guard: block if tarikh_inbois is after tarikh_tamat of the selected account
    try {
      const tamat = selectedAkaun?.tarikh_tamat || selectedAkaun?.tarikhtamat || selectedAkaun?.tarikhTamat;
      if (tamat) {
        const tInv = new Date(tarikh_inbois);
        const tTamat = new Date(tamat);
        if (!isNaN(tInv) && !isNaN(tTamat) && tInv > tTamat) {
          const dd = String(tTamat.getDate()).padStart(2, '0');
          const mm = String(tTamat.getMonth() + 1).padStart(2, '0');
          const yyyy = tTamat.getFullYear();
          showError(`Ralat. Tarikh tamat sewaan adalah pada ${dd}/${mm}/${yyyy}`);
          return;
        }
      }
    } catch {}
    setSaving(true);
    try {
      // Fetch akaun details for kadar_sewa and kategori_kutipan
      const noAkaunRef = selectedAkaun?.noakaun || selectedAkaun?.no_akaun || "";
      let akaunDetails;
      try {
        const res = await apiGetAkaunByNoAkaun(noAkaunRef);
        akaunDetails = Array.isArray(res?.data) ? res.data[0] : res?.data;
      } catch (err) {
        showError("Gagal mendapatkan maklumat akaun untuk Kadar Sewa dan Kategori Kutipan.");
        setSaving(false);
        return;
      }
      if (!akaunDetails || !akaunDetails.kadar_sewa || !akaunDetails.kategori_kutipan) {
        showError("Maklumat Kadar Sewa atau Kategori Kutipan tidak lengkap pada akaun ini.");
        setSaving(false);
        return;
      }
      const isoDate = new Date(tarikh_inbois).toISOString();
  // Compose perkara: 'kategori_kutipan MM/YYYY'
  // Today's date ISO string for tarikh_simpan
  const todayIso = new Date().toISOString();
      let perkaraStr = "";
      if (akaunDetails.kategori_kutipan && tarikh_inbois) {
  const d = new Date(tarikh_inbois);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  perkaraStr = `${akaunDetails.kategori_kutipan} ${mm}/${yyyy}`;
  // We'll use perkaraStr for inbois, and 'INV: ' + perkaraStr for lejar
      }
      // Get kadar_sewa for jumlah/debit
      const kadarSewa = parseFloat(akaunDetails.kadar_sewa) || 0;
      // Use newNoInbois generated from apiGetMaxNoInbois
      if (!newNoInbois) {
        showError("No Inbois tidak dapat dijana. Sila cuba lagi.");
        setSaving(false);
        return;
      }
      const noInbois = newNoInbois;

      // Check for duplicate perkara for this month/year in inbois or lejar
      // Search by perkara and no_akaun (which is noAkaunRef)
      let existsInInbois = false;
      let existsInLejar = false;
      try {
        // Check inbois table
        const resInbois = await fetch(`/api/inbois/noakaun/${noAkaunRef}`);
        if (resInbois.ok) {
          const data = await resInbois.json();
          if (Array.isArray(data.data)) {
            existsInInbois = data.data.some((row) => row.perkara === perkaraStr);
          }
        }
      } catch {}
      try {
        // Check lejar table
        const resLejar = await fetch(`/api/lejar/noakaun/${noAkaunRef}`);
        if (resLejar.ok) {
          const data = await resLejar.json();
          if (Array.isArray(data.data)) {
            existsInLejar = data.data.some((row) => row.perkara === perkaraStr);
          }
        }
      } catch {}
      if (existsInInbois || existsInLejar) {
        let msg = "Inbois bulan terpilih telah dijana.";
        if (existsInInbois && !existsInLejar)
          msg = "Inbois bulan terpilih telah dijana dalam jadual inbois.";
        if (!existsInInbois && existsInLejar)
          msg = "Inbois bulan terpilih telah dijana dalam jadual lejar.";
        showError(msg);
        setSaving(false);
        return;
      }
      const payload = {
        mark_batal: 0,
        mark_delete: 0,
  tarikh_inbois: isoDate,
  tarikh_simpan: todayIso,
        tarikh_bayar: null,
        tarikh_terakhir_jana: "",
        jumlah: kadarSewa,
        jumlah_total_detail: 0.0,
        perkara: perkaraStr,
        mark_post: 1,
        bahagian_pemantau: user.bahagian || "",
        id_pengguna: (user.id_pengguna || "").toLowerCase(),
        id_inbois_bahagian: 0,
        id_akaun: 0,
        no_akaun: noAkaunRef,
        no_inbois: noInbois,
        kod_harta: akaunDetails.kodharta || akaunDetails.kod_harta || "",
        bil_hari_lewat_bayar: 0,
        notis_pertama: 0,
        status_notis_pertama: 0,
        notis_kedua: 0,
        status_notis_kedua: 0,
        notis_ketiga: 0,
        status_notis_ketiga: 0,
        the_diff: 0,
        aging_till6: 0,
        aging7_12: 0,
        aging13_18: 0,
        aging19_24: 0,
        aging25_30: 0,
        aging31_36: 0,
        aging37_72: 0,
        aging73_120: 0,
        aging_more120: 0,
        no_resit: "",
        kategori_inbois: "Single",
        tunggakan_to_date: 0.0,
        denda_to_date: 0.0,
        baki_akhir_lejar: 0.0,
        peratus_diskaun: 0.0,
        mark_bayar: 0,
        jumlah_bayar: 0.0,
      };
      await apiTambahInbois(payload);
      // --- Call lejar API after inbois is created ---
      const lejarPayload = {
        no_akaun: noAkaunRef,
        kod_harta: akaunDetails.kodharta || akaunDetails.kod_harta || "",
        tarikh_inbois: isoDate,
        tarikh_simpan: todayIso,
        tarikh_resit: isoDate,
        doc_date: isoDate,
        no_inbois: noInbois,
        perkara: `INV: ${perkaraStr}`,
        debit: kadarSewa,
        id_pengguna: (user.id_pengguna || "").toLowerCase(),
        bahagian_pemantau: user.bahagian || "",
      };
      await apiTambahLejar(lejarPayload);
      showSuccess("Inbois bulan terpilih telah berjaya di jana.");
      if (onSuccess) onSuccess();
      onClose();
    } catch (e) {
      handleApiError(e, showError);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setTarikhInbois("");
    setError("");
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={dialogHeaderStyles.success}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ color: "#fff" }}>Inbois Sewaan</Typography>
        </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ ...dialogContentStyles.light, p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <TextField
                label="Tarikh Inbois"
                type="date"
                value={tarikh_inbois}
                onChange={(e) => setTarikhInbois(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                size="small"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={dialogContentStyles.light}>
          <Grid
            item
            xs={12}
            md={6}
            lg={6}
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
          >
            <Button
              onClick={handleSimpan}
              variant="contained"
              disabled={saving}
              size="small"
              sx={buttonStyles.save}
            >
              SIMPAN
            </Button>
            <Button
              onClick={handleClose}
              variant="contained"
              size="small"
              sx={buttonStyles.cancel}
            >
              BATAL
            </Button>
          </Grid>
        </DialogActions>
      </Dialog>
    </>
  );
}

TambahInbois.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedAkaun: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default TambahInbois;
